/**
 * A tar, built in the browser, of the files of one slide.
 *
 * A scanner exports a slide as several DICOM files (one per pyramid level, plus
 * its label and overview). They are sent as ONE file, so the slide is one image
 * with one upload, one storage check, one conversion and one outcome
 * (server/src/http/uploads.ts; convert sources/tar_bundle.py unpacks it). A tar
 * needs no checksum of its content and no index: each member is a 512-byte
 * header followed by the file's own bytes, so the archive is a Blob of headers
 * and of the files themselves, referenced and never copied, and a part of it
 * is a slice of it.
 *
 * DETERMINISTIC: the same files (name, size, last-modified time), picked in any
 * order, make byte-for-byte the same tar, so an upload cut by a reload resumes
 * from its confirmed parts once the files are selected again (the server keeps
 * the parts; the page rebuilds the rest).
 *
 * ustar headers; a name over 100 bytes goes in a pax `path` record ahead of its
 * header, a size of 8 GiB or more in base 256 (GNU); Python's tarfile reads
 * both. Two members cannot share a name (the converter refuses that), so a
 * second file of one name is suffixed " (2)" before its extension.
 */

/** One file of the tar, as its identity: what a re-selected file is matched on. */
export interface TarMember {
  name: string;
  size: number;
  lastModified: number;
}

export interface Tar {
  blob: Blob;
  /** The tar's size in bytes, what the upload is planned for. */
  size: number;
  /** Its members, in tar order (by name). */
  members: TarMember[];
}

const BLOCK = 512;
const NAME_MAX = 100;
/** One past the largest size an octal field of eleven digits holds: 8 GiB. */
const OCTAL_MAX = 8 ** 11;
const encoder = new TextEncoder();

const padded = (n: number) => Math.ceil(n / BLOCK) * BLOCK;

function octal(v: number, width: number): Uint8Array {
  return encoder.encode(v.toString(8).padStart(width - 1, '0') + '\0');
}

/** GNU base 256: a leading 0x80, then the value big-endian in the rest of the field. */
function base256(v: number, width: number): Uint8Array {
  const out = new Uint8Array(width);
  out[0] = 0x80;
  let rest = BigInt(v);
  for (let i = width - 1; i > 0; i--) {
    out[i] = Number(rest & 0xffn);
    rest >>= 8n;
  }
  return out;
}

/** A ustar header block. `mtime` in seconds. Exported for its tests. */
export function tarHeader(name: string, size: number, mtime: number, typeflag = '0'): Uint8Array<ArrayBuffer> {
  const h = new Uint8Array(BLOCK);
  h.set(encoder.encode(name).subarray(0, NAME_MAX), 0);
  h.set(octal(0o644, 8), 100);
  h.set(octal(0, 8), 108);
  h.set(octal(0, 8), 116);
  h.set(size < OCTAL_MAX ? octal(size, 12) : base256(size, 12), 124);
  h.set(octal(mtime, 12), 136);
  h.fill(0x20, 148, 156); // the checksum field counts as spaces while summing
  h[156] = typeflag.charCodeAt(0);
  h.set(encoder.encode('ustar\0'), 257);
  h.set(encoder.encode('00'), 263);
  let sum = 0;
  for (const b of h) sum += b;
  h.set(encoder.encode(sum.toString(8).padStart(6, '0') + '\0 '), 148);
  return h;
}

/** A pax extended header carrying a name over 100 bytes, ahead of the member's own header. */
function paxFor(name: string, mtime: number): Uint8Array<ArrayBuffer>[] {
  const body = encoder.encode(` path=${name}\n`);
  // A record starts with its own length, that length's digits included.
  let total = body.length + String(body.length).length;
  if (String(total).length > String(body.length).length) total += 1;
  const record = new Uint8Array(padded(total));
  record.set(encoder.encode(String(total)), 0);
  record.set(body, String(total).length);
  return [tarHeader(`PaxHeaders/${name}`, total, mtime, 'x'), record];
}

/** The name a member goes by: its file's, or with " (n)" when another member has it already. */
function uniqueName(name: string, taken: ReadonlySet<string>): string {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
  for (let n = 2; ; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Tar order: by name, then size, then time, so the same files make the same archive whatever order they were picked in. */
const byName = (a: TarMember, b: TarMember) => (a.name < b.name ? -1 : a.name > b.name ? 1 : a.size - b.size || a.lastModified - b.lastModified);

/**
 * The tar of these files: their bytes, in name order, referenced. `paths`
 * names each file inside the tar where that is not its own name: a MIRAX
 * keeps its data folder (`Slide/Data0000.dat`); a DICOM set is flat.
 */
export function tarOf(files: readonly File[], paths?: readonly string[]): Tar {
  const taken = new Set<string>();
  const parts: BlobPart[] = [];
  const members: TarMember[] = [];
  let size = 0;
  const named = files.map((file, i) => ({ file, name: paths?.[i] || file.name, size: file.size, lastModified: file.lastModified }));
  for (const { file: f, name: given } of named.sort(byName)) {
    const name = uniqueName(given, taken);
    taken.add(name);
    const mtime = Math.max(0, Math.floor((f.lastModified || 0) / 1000));
    const head = encoder.encode(name).length > NAME_MAX ? [...paxFor(name, mtime), tarHeader(name, f.size, mtime)] : [tarHeader(name, f.size, mtime)];
    const padding = new Uint8Array(padded(f.size) - f.size);
    parts.push(...head, f, padding);
    for (const h of head) size += h.length;
    size += f.size + padding.length;
    members.push({ name, size: f.size, lastModified: f.lastModified });
  }
  const end = new Uint8Array(2 * BLOCK);
  parts.push(end);
  size += end.length;
  return { blob: new Blob(parts, { type: 'application/x-tar' }), size, members };
}

/** What a tar's members are: a MIRAX slide when one is its .mrxs, else the DICOM files of one series. */
export const holdsOf = (members: readonly TarMember[]): 'dcm' | 'mrxs' => (members.some((m) => /\.mrxs$/i.test(m.name)) ? 'mrxs' : 'dcm');

/** Whether two member lists are the same files: the same names, sizes and last-modified times. A time of 0 is unknown and matches any. */
export function sameMembers(a: readonly TarMember[], b: readonly TarMember[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort(byName);
  const y = [...b].sort(byName);
  return x.every((m, i) => m.name === y[i].name && m.size === y[i].size && (m.lastModified === 0 || y[i].lastModified === 0 || m.lastModified === y[i].lastModified));
}
